import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Fantasy (Arena) PR-A foundation endpoints. All gated server-side by
// checkUser; reads come from the MySQL mirror (fantasy_* tables). The draft
// pool is empty until PR-B's pricing job runs.
export const fantasyApi = createApi({
  reducerPath: "fantasyApi",
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "FantasyMine",
    "FantasyStandings",
    "FantasyAdmin",
    "FantasyPricing",
    "FantasyActivity",
    "FantasyLeagues",
    "FantasyChat",
    "FantasyRecap",
    "FantasyMatchup",
    "FantasyPowerups",
    "FantasyTrades",
    "FantasyWaiver",
    "FantasyProps",
    "FantasyPushPrefs",
    "FantasyDraftRoom",
    "FantasyScoring",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/fantasy/portal/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getMyFantasy: builder.query({
      query: () => "mine",
      providesTags: ["FantasyMine"],
    }),
    getDraftPool: builder.query({
      query: () => "pool",
    }),
    draftRoster: builder.mutation({
      query: (repRecids) => ({ url: "draft", method: "POST", body: { repRecids } }),
      invalidatesTags: ["FantasyMine", "FantasyStandings"],
    }),
    swapRep: builder.mutation({
      query: ({ outSlot, newRepRecid }) => ({
        url: "swap",
        method: "POST",
        body: { outSlot, newRepRecid },
      }),
      invalidatesTags: ["FantasyMine", "FantasyStandings"],
    }),
    // Free replacement for a slot whose rep quit (vacated_at set server-side).
    // Does NOT consume the one mid-season swap.
    replaceRep: builder.mutation({
      query: ({ slot, newRepRecid }) => ({
        url: "replace",
        method: "POST",
        body: { slot, newRepRecid },
      }),
      invalidatesTags: ["FantasyMine", "FantasyStandings"],
    }),
    // v7 #38 — accepts either a board string ("total") or an object
    // { board, leagueId }. leagueId scopes the board to one mini-league; absent
    // = global board (backward compatible with existing string callers).
    getStandings: builder.query({
      query: (arg = "total") => {
        const board = typeof arg === "string" ? arg : arg?.board || "total";
        const leagueId = typeof arg === "string" ? null : arg?.leagueId;
        const p = new URLSearchParams({ board });
        if (leagueId != null && leagueId !== "") p.set("leagueId", String(leagueId));
        return `standings?${p.toString()}`;
      },
      providesTags: ["FantasyStandings"],
    }),

    // v7 #37 — head-to-head weekly matchups + end-of-season playoffs. Reads
    // served from the MySQL mirror (fantasy_matchup / _record / _playoff);
    // mounted under the /matchup sub-path of the fantasy route.
    getMyMatchup: builder.query({
      query: () => "matchup/mine",
      providesTags: ["FantasyMatchup", "FantasyMine"],
    }),
    getMatchupStandings: builder.query({
      query: () => "matchup/standings",
      providesTags: ["FantasyMatchup", "FantasyStandings"],
    }),
    // v7 #37 — week-by-week breakdown behind one manager's W/L/T + Pts For (the
    // tappable "how was this number earned" drill-in on the standings table).
    getMatchupBreakdown: builder.query({
      query: (managerId) => `matchup/breakdown?managerId=${encodeURIComponent(managerId)}`,
      providesTags: ["FantasyMatchup"],
    }),
    getPlayoffBracket: builder.query({
      query: () => "matchup/playoff",
      providesTags: ["FantasyMatchup"],
    }),
    // v7 #38 — mini-leagues. Player read: leagues in the season + the caller's
    // own league (for the switcher + "your league" badge).
    getLeagues: builder.query({
      query: () => "leagues",
      providesTags: ["FantasyLeagues"],
    }),
    // Admin read: leagues with member rosters + the unassigned pool.
    getAdminLeagues: builder.query({
      query: () => "admin/leagues",
      providesTags: ["FantasyLeagues", "FantasyAdmin"],
    }),
    createLeague: builder.mutation({
      query: (body) => ({ url: "admin/leagues", method: "POST", body }),
      invalidatesTags: ["FantasyLeagues", "FantasyStandings"],
    }),
    renameLeague: builder.mutation({
      query: (body) => ({ url: "admin/league/rename", method: "POST", body }),
      invalidatesTags: ["FantasyLeagues"],
    }),
    deleteLeague: builder.mutation({
      query: (leagueId) => ({ url: "admin/league/delete", method: "POST", body: { leagueId } }),
      invalidatesTags: ["FantasyLeagues", "FantasyStandings"],
    }),
    moveManager: builder.mutation({
      query: (body) => ({ url: "admin/league/move", method: "POST", body }),
      invalidatesTags: ["FantasyLeagues", "FantasyStandings"],
    }),
    autoBalanceLeagues: builder.mutation({
      query: (body = {}) => ({ url: "admin/league/auto-balance", method: "POST", body }),
      invalidatesTags: ["FantasyLeagues", "FantasyStandings"],
    }),
    // v7 #11 — live draft-room ("draft night"). The room-state read runs the
    // server-side lazy clock, so short-polling this query (pollingInterval set
    // at the hook call-site) is what advances every client's countdown.
    getDraftRoom: builder.query({
      query: (roomId) => (roomId ? `draft-room?roomId=${encodeURIComponent(roomId)}` : "draft-room"),
      providesTags: ["FantasyDraftRoom"],
    }),
    submitDraftRoomPick: builder.mutation({
      query: ({ repRecid, roomId }) => ({
        url: "draft-room/pick",
        method: "POST",
        body: { repRecid, roomId },
      }),
      invalidatesTags: ["FantasyDraftRoom", "FantasyMine", "FantasyStandings"],
    }),
    // Admin draft-room controls (Tournament Admin "Draft Night" menu).
    scheduleDraftRoom: builder.mutation({
      query: ({ scheduledAt, pickSeconds }) => ({
        url: "admin/draft-room/schedule",
        method: "POST",
        body: { scheduledAt, pickSeconds },
      }),
      invalidatesTags: ["FantasyDraftRoom", "FantasyAdmin"],
    }),
    startDraftRoom: builder.mutation({
      query: (roomId) => ({ url: "admin/draft-room/start", method: "POST", body: { roomId } }),
      invalidatesTags: ["FantasyDraftRoom", "FantasyAdmin"],
    }),
    cancelDraftRoom: builder.mutation({
      query: (roomId) => ({ url: "admin/draft-room/cancel", method: "POST", body: { roomId } }),
      invalidatesTags: ["FantasyDraftRoom", "FantasyAdmin"],
    }),

    // feature #36 — scoring legend + the caller's own rostered reps' daily
    // points breakdown (server-scoped to the authenticated manager's roster).
    getMyLedger: builder.query({
      query: () => "my-ledger",
      providesTags: ["FantasyMine"],
    }),

    // feature #50 — the authenticated rep's OWN "you're a star" fame card:
    // managers who rostered them, anonymized ownership rank, draft-price trend,
    // total fantasy points. Server-scoped to req.user; returns isRep:false for a
    // caller who isn't a draftable rep this season.
    getMyOwnership: builder.query({
      query: () => "my-ownership",
      providesTags: ["FantasyMine"],
    }),

    // feature #43 — latest "Week in Review" recap markdown (read from MySQL).
    getWeekInReview: builder.query({
      query: () => "week-in-review",
      providesTags: ["FantasyRecap"],
    }),
    // feature #48 — power-up tokens. Wallet (catalog + remaining + applied
    // tokens w/ their computed scoring delta) and the spend mutation. The spend
    // changes standings at the aggregation layer, so invalidate the boards too.
    getPowerups: builder.query({
      query: () => "powerups",
      providesTags: ["FantasyPowerups"],
    }),
    spendPowerup: builder.mutation({
      query: ({ code, targetRepRecid, weekStart }) => ({
        url: "powerups/spend",
        method: "POST",
        body: { code, targetRepRecid, weekStart },
      }),
      invalidatesTags: ["FantasyPowerups", "FantasyStandings", "FantasyMine"],
    }),

    // v7 #40 — trades + weekly waiver wire (player-facing).
    getLeagueRosters: builder.query({
      query: () => "rosters",
      providesTags: ["FantasyTrades"],
    }),
    getTrades: builder.query({
      query: () => "trades",
      providesTags: ["FantasyTrades"],
    }),
    proposeTrade: builder.mutation({
      query: (body) => ({ url: "trades/propose", method: "POST", body }),
      invalidatesTags: ["FantasyTrades"],
    }),
    acceptTrade: builder.mutation({
      query: (tradeId) => ({ url: "trades/accept", method: "POST", body: { tradeId } }),
      invalidatesTags: ["FantasyTrades", "FantasyMine", "FantasyStandings"],
    }),
    rejectTrade: builder.mutation({
      query: (tradeId) => ({ url: "trades/reject", method: "POST", body: { tradeId } }),
      invalidatesTags: ["FantasyTrades"],
    }),
    getWaiver: builder.query({
      query: () => "waiver",
      providesTags: ["FantasyWaiver"],
    }),
    submitWaiverClaim: builder.mutation({
      query: (body) => ({ url: "waiver/claim", method: "POST", body }),
      // Instant waivers: a granted claim also swaps the roster, so invalidate
      // FantasyMine (roster + cap) alongside FantasyWaiver (free-agent pool).
      invalidatesTags: ["FantasyWaiver", "FantasyMine", "FantasyStandings"],
    }),
    cancelWaiverClaim: builder.mutation({
      query: () => ({ url: "waiver/cancel", method: "POST" }),
      invalidatesTags: ["FantasyWaiver"],
    }),

    // feature #39 — live-scoring push opt-in ("your rep just scored!").
    getPushPrefs: builder.query({
      query: () => "push-prefs",
      providesTags: ["FantasyPushPrefs"],
    }),
    setPushPref: builder.mutation({
      query: (body) => ({ url: "push-prefs", method: "POST", body }),
      invalidatesTags: ["FantasyPushPrefs"],
    }),
    // v7 feature #42 — the caller's earned + locked achievement badges. Awards
    // are written by the daily rollup; this is a read of the current state.
    getBadges: builder.query({
      query: () => "badges",
      providesTags: ["FantasyMine"],
    }),

    // feature #44 — player-card metadata (photo, market, points sparkline,
    // hot/cold stock trend) keyed by rep_recid; joined onto the draft pool +
    // roster rows the UI already has.
    getRepCards: builder.query({
      query: () => "rep-cards",
    }),

    // v6 admin shell (Tournament Admin "Fantasy Sales League"). Reads served
    // from the MySQL ledger; gated server-side by checkUser, front-end by TBAC.
    getAdminDashboard: builder.query({
      query: () => "admin/dashboard",
      providesTags: ["FantasyAdmin"],
    }),
    getAdminRepLedger: builder.query({
      query: (repName) => `admin/rep-ledger?repName=${encodeURIComponent(repName)}`,
      providesTags: ["FantasyAdmin"],
    }),
    getAdminRosters: builder.query({
      query: () => "admin/rosters",
      providesTags: ["FantasyAdmin"],
    }),
    getAdminOwnership: builder.query({
      query: () => "admin/ownership",
      providesTags: ["FantasyAdmin"],
    }),

    // v6 admin menu #5 — draft-board / pricing manager.
    getAdminPricing: builder.query({
      query: () => "admin/pricing",
      providesTags: ["FantasyAdmin", "FantasyPricing"],
    }),
    runPricing: builder.mutation({
      query: () => ({ url: "admin/price-run", method: "POST" }),
      invalidatesTags: ["FantasyAdmin", "FantasyPricing"],
    }),
    overrideRepPrice: builder.mutation({
      query: ({ repRecid, priceCredits }) => ({
        url: "admin/price-override",
        method: "POST",
        body: { repRecid, priceCredits },
      }),
      invalidatesTags: ["FantasyAdmin", "FantasyPricing"],
    }),
    // v6 menu #8 — manual score correction. Mutations re-fetch the rep ledger
    // (FantasyAdmin) and the audit feed (FantasyActivity).
    addLedgerLine: builder.mutation({
      query: (body) => ({ url: "admin/ledger/add", method: "POST", body }),
      invalidatesTags: ["FantasyAdmin", "FantasyActivity", "FantasyStandings"],
    }),
    voidLedgerLine: builder.mutation({
      query: (body) => ({ url: "admin/ledger/void", method: "POST", body }),
      invalidatesTags: ["FantasyAdmin", "FantasyActivity", "FantasyStandings"],
    }),
    getAdminActivity: builder.query({
      query: ({ repName = "", limit = 100 } = {}) => {
        const p = new URLSearchParams();
        if (repName) p.set("repName", repName);
        if (limit) p.set("limit", String(limit));
        const qs = p.toString();
        return `admin/activity${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["FantasyActivity"],
    }),
    // Menu #6 drill-in: all three boards in one payload.
    getAdminStandings: builder.query({
      query: () => "admin/standings",
      providesTags: ["FantasyAdmin"],
    }),
    // Menu #6 drill-in: one manager's per-rep contribution to their total.
    getAdminManagerContribution: builder.query({
      query: (managerId) => `admin/manager-contribution?managerId=${encodeURIComponent(managerId)}`,
      providesTags: ["FantasyAdmin"],
    }),
    // Menu #10 — unified activity feed (scoring runs + drafts + swaps + admin
    // actions). Distinct from menu #8's admin/activity corrections-audit feed.
    getAdminActivityFeed: builder.query({
      query: (limit = 100) => `admin/activity-feed?limit=${encodeURIComponent(limit)}`,
      providesTags: ["FantasyAdmin"],
    }),
    // v6 admin menu #9 — Manager controls. List managers with roster/swap/
    // termination status; force-unlock a roster, grant an extra swap, or remove
    // a terminated employee from the league. Server gates removal (409) unless
    // the employee is marked terminated in the Employees mirror.
    getAdminManagers: builder.query({
      query: () => "admin/managers",
      providesTags: ["FantasyAdmin"],
    }),
    unlockManager: builder.mutation({
      query: (managerId) => ({
        url: "admin/manager/unlock",
        method: "POST",
        body: { managerId },
      }),
      invalidatesTags: ["FantasyAdmin", "FantasyStandings"],
    }),
    grantManagerSwap: builder.mutation({
      query: (managerId) => ({
        url: "admin/manager/grant-swap",
        method: "POST",
        body: { managerId },
      }),
      invalidatesTags: ["FantasyAdmin"],
    }),
    // Pass force:true to remove an ACTIVE manager (the controls UI gates that
    // behind a danger-styled confirm). Without force the server still refuses
    // (409) anyone not marked terminated.
    removeManager: builder.mutation({
      query: ({ managerId, force = false }) => ({
        url: "admin/manager/remove",
        method: "POST",
        body: { managerId, force },
      }),
      invalidatesTags: ["FantasyAdmin", "FantasyStandings"],
    }),
    // Manually drop one rep from the draft pool (admin "remove player").
    removeRep: builder.mutation({
      query: (repRecid) => ({
        url: "admin/rep/remove",
        method: "POST",
        body: { repRecid },
      }),
      invalidatesTags: ["FantasyAdmin", "FantasyPricing"],
    }),

    // v6 admin menu #7 — idempotent re-run of scoring for one date (writes the
    // ledger). Invalidates FantasyAdmin so the dashboard "last scored" refreshes.
    rerunScoring: builder.mutation({
      query: ({ date }) => ({ url: "admin/rerun-scoring", method: "POST", body: { date } }),
      invalidatesTags: ["FantasyAdmin"],
    }),

    // FSL "metric changer" (Levi 2026-06-09) — admin reads/writes the point
    // value of every scoring metric. Backend: pestroutes.fantasy_metric_config
    // (V041), read live by the scoring engine with safe fallback to code
    // defaults. getScoringConfig returns { updatedAt, metrics:[{key,label,unit,
    // kind,points}] }.
    getScoringConfig: builder.query({
      query: () => "admin/scoring-config",
      providesTags: ["FantasyScoring"],
    }),
    // body { updates: { metric_key: number } }. New values apply to FUTURE
    // scoring automatically; Re-run (menu #7) reapplies them to scored dates.
    saveScoringConfig: builder.mutation({
      query: (updates) => ({ url: "admin/scoring-config", method: "POST", body: { updates } }),
      invalidatesTags: ["FantasyScoring"],
    }),

    // v7 #41 — chat / trash-talk. Board addressed by scope ('global' | 'league'
    // | 'matchup') + scopeId. With no leagues/matchups defined only the global
    // board is surfaced (siblings #37/#38 may not be merged).
    getChat: builder.query({
      query: ({ scope = "global", scopeId = "", limit = 100 } = {}) => {
        const p = new URLSearchParams();
        p.set("scope", scope);
        if (scopeId) p.set("scopeId", scopeId);
        if (limit) p.set("limit", String(limit));
        return `chat?${p.toString()}`;
      },
      providesTags: ["FantasyChat"],
    }),
    postChat: builder.mutation({
      query: ({ scope = "global", scopeId = "", body }) => ({
        url: "chat",
        method: "POST",
        body: { scope, scopeId, body },
      }),
      invalidatesTags: ["FantasyChat"],
    }),
    // Admin moderation — list (incl. hidden), hide, restore.
    getAdminChat: builder.query({
      query: ({ scope = "global", scopeId = "", limit = 100, includeDeleted = true } = {}) => {
        const p = new URLSearchParams();
        p.set("scope", scope);
        if (scopeId) p.set("scopeId", scopeId);
        if (limit) p.set("limit", String(limit));
        p.set("includeDeleted", String(includeDeleted));
        return `admin/chat?${p.toString()}`;
      },
      providesTags: ["FantasyChat"],
    }),
    hideChatMessage: builder.mutation({
      query: ({ messageId, reason }) => ({
        url: "admin/chat/hide",
        method: "POST",
        body: { messageId, reason },
      }),
      invalidatesTags: ["FantasyChat"],
    }),
    unhideChatMessage: builder.mutation({
      query: ({ messageId }) => ({
        url: "admin/chat/unhide",
        method: "POST",
        body: { messageId },
      }),
      invalidatesTags: ["FantasyChat"],
    }),
    // v7 #49 — weekly prediction props (bonus points; separate from roster math).
    // Player: this week's open questions + the caller's picks + running props
    // bonus. Admin: author / close / settle questions; settle grades against the
    // ledger (settle-context surfaces DM/point leaders + max streak as a hint).
    getProps: builder.query({
      query: () => "props",
      providesTags: ["FantasyProps"],
    }),
    submitPropPick: builder.mutation({
      query: ({ propId, answer }) => ({
        url: "props/pick",
        method: "POST",
        body: { propId, answer },
      }),
      invalidatesTags: ["FantasyProps"],
    }),
    getAdminProps: builder.query({
      query: () => "admin/props",
      providesTags: ["FantasyProps", "FantasyAdmin"],
    }),
    createProp: builder.mutation({
      query: (body) => ({ url: "admin/props", method: "POST", body }),
      invalidatesTags: ["FantasyProps", "FantasyAdmin"],
    }),
    closeProp: builder.mutation({
      query: ({ propId }) => ({ url: "admin/props/close", method: "POST", body: { propId } }),
      invalidatesTags: ["FantasyProps", "FantasyAdmin"],
    }),
    settleProp: builder.mutation({
      query: ({ propId, correctAnswer }) => ({
        url: "admin/props/settle",
        method: "POST",
        body: { propId, correctAnswer },
      }),
      invalidatesTags: ["FantasyProps", "FantasyAdmin"],
    }),
    getPropSettleContext: builder.query({
      query: (propId) => `admin/props/settle-context?propId=${encodeURIComponent(propId)}`,
      providesTags: ["FantasyProps"],
    }),
    // Build #3 — Giddy Up! / Attaboy! motivate buttons on My Roster rep cards.
    // POST body: { slot, type: 'giddy_up'|'attaboy', betAmount? }
    // The roster response already includes per-rep motivate state from getMine,
    // so no separate GET hook is needed — just the mutation.
    postMotivate: builder.mutation({
      query: (body) => ({ url: "motivate", method: "POST", body }),
      invalidatesTags: ["FantasyMine", "FantasyStandings"],
    }),
  }),
});

export const {
  useGetMyFantasyQuery,
  useGetDraftPoolQuery,
  useDraftRosterMutation,
  useSwapRepMutation,
  useReplaceRepMutation,
  useGetDraftRoomQuery,
  useSubmitDraftRoomPickMutation,
  useScheduleDraftRoomMutation,
  useStartDraftRoomMutation,
  useCancelDraftRoomMutation,
  useGetStandingsQuery,
  useGetMyMatchupQuery,
  useGetMatchupStandingsQuery,
  useGetMatchupBreakdownQuery,
  useGetPlayoffBracketQuery,
  useGetLeaguesQuery,
  useGetAdminLeaguesQuery,
  useCreateLeagueMutation,
  useRenameLeagueMutation,
  useDeleteLeagueMutation,
  useMoveManagerMutation,
  useAutoBalanceLeaguesMutation,
  useGetMyLedgerQuery,
  useGetMyOwnershipQuery,
  useGetWeekInReviewQuery,
  useGetRepCardsQuery,
  useGetPowerupsQuery,
  useSpendPowerupMutation,
  useGetLeagueRostersQuery,
  useGetTradesQuery,
  useProposeTradeMutation,
  useAcceptTradeMutation,
  useRejectTradeMutation,
  useGetWaiverQuery,
  useSubmitWaiverClaimMutation,
  useCancelWaiverClaimMutation,
  useGetPushPrefsQuery,
  useSetPushPrefMutation,
  useGetBadgesQuery,
  useGetAdminDashboardQuery,
  useGetAdminRepLedgerQuery,
  useGetAdminRostersQuery,
  useGetAdminOwnershipQuery,
  useGetAdminPricingQuery,
  useRunPricingMutation,
  useOverrideRepPriceMutation,
  useAddLedgerLineMutation,
  useVoidLedgerLineMutation,
  useGetAdminActivityQuery,
  useGetAdminStandingsQuery,
  useGetAdminManagerContributionQuery,
  useGetAdminActivityFeedQuery,
  useGetAdminManagersQuery,
  useUnlockManagerMutation,
  useRemoveRepMutation,
  useGrantManagerSwapMutation,
  useRemoveManagerMutation,
  useRerunScoringMutation,
  // FSL metric changer (scoring config)
  useGetScoringConfigQuery,
  useSaveScoringConfigMutation,
  // v7 #41 — chat
  useGetChatQuery,
  usePostChatMutation,
  useGetAdminChatQuery,
  useHideChatMessageMutation,
  useUnhideChatMessageMutation,
  // v7 #49 — props
  useGetPropsQuery,
  useSubmitPropPickMutation,
  useGetAdminPropsQuery,
  useCreatePropMutation,
  useClosePropMutation,
  useSettlePropMutation,
  useLazyGetPropSettleContextQuery,
  // Build #3 — Giddy Up! / Attaboy! motivate buttons
  usePostMotivateMutation,
} = fantasyApi;
